import { Box, Chip } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo, useState } from "react";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import type { StandRow } from "../models/TrendDisplay";

interface RFactorChartProps {
	stands: StandRow[];
}

const COLOR_RECIPE = "#2A32FF";
const COLOR_ACTUAL = "#244200";
const COLOR_BAND = "#E60000";

const SERIES_COLORS = {
	Recipe: COLOR_RECIPE,
	Actual: COLOR_ACTUAL,
	Max: COLOR_BAND,
	Min: COLOR_BAND,
} as const;

type SeriesName = keyof typeof SERIES_COLORS;
const SERIES_NAMES: SeriesName[] = ["Recipe", "Actual", "Max", "Min"];

export const RFactorChart = ({ stands }: RFactorChartProps) => {
	const [visible, setVisible] = useState<Record<SeriesName, boolean>>({
		Recipe: true,
		Actual: true,
		Max: true,
		Min: true,
	});

	const toggle = (name: SeriesName) =>
		setVisible((prev) => ({ ...prev, [name]: !prev[name] }));

	const option = useMemo(() => {
		const standNames = stands.map((s) => s.Stand);

		// Pass the raw values straight through — matches the legacy IHBox
		// chart, which plots the historian's RFactorCurrent for every stand
		// regardless of the Active flag (so inactive stands still get a
		// green dot, producing the characteristic "valley" the operator
		// expects to see when a stand is dropped from the recipe).
		// Recipe/Min/Max are already null at the SP level when the stand
		// is not in ProductMillSetup, so they fall through naturally.
		const targets = stands.map((s) => s.RFactorTarget);
		const actuals = stands.map((s) => s.RFactorCurrent);
		const maxes = stands.map((s) => s.RFactorMax);
		const mins = stands.map((s) => s.RFactorMin);

		// IMPORTANT: pass null directly (not wrapped in {value:null,symbol:'none'}).
		// ECharts treats wrapped null differently for connectNulls — it can
		// still draw the marker / interpolate. A bare `null` in the data
		// array is correctly skipped: no marker, line connects across.
		// Markers on real values stay enabled by default (showSymbol=true).
		const toSeriesData = (arr: (number | null)[]) => arr;

		return {
			// Instant first render, smooth transitions on data update so the
			// operator can see the line move when the SP refetches every 4 s.
			animation: true,
			animationDuration: 0,
			animationDurationUpdate: 400,
			animationEasingUpdate: "cubicOut",
			tooltip: {
				trigger: "axis",
				valueFormatter: (v: number | null) =>
					v == null ? "—" : Number(v).toFixed(3),
			},
			// Built-in legend hidden — visibility driven by the MUI Chips above.
			legend: { show: false, selected: visible },
			grid: { left: 50, right: 24, top: 12, bottom: 32 },
			xAxis: {
				type: "category",
				data: standNames,
				axisLabel: { fontSize: 11 },
			},
			yAxis: {
				type: "value",
				min: 0.8,
				max: 1.7,
				interval: 0.1,
				axisLabel: { formatter: (v: number) => v.toFixed(2) },
			},
			series: [
				// connectNulls: true matches the legacy IHBox behaviour — Chart.js v1
				// silently joined adjacent points across nulls, yielding a single
				// continuous line per series even when intermediate stands are
				// inactive. ECharts default is the opposite (explicit gaps), so
				// we flip it on all 4 series for visual parity.
				{
					name: "Recipe",
					type: "line",
					data: toSeriesData(targets),
					itemStyle: { color: COLOR_RECIPE },
					lineStyle: { color: COLOR_RECIPE, width: 2 },
					symbolSize: 6,
					connectNulls: true,
				},
				{
					name: "Actual",
					type: "line",
					data: toSeriesData(actuals),
					itemStyle: { color: COLOR_ACTUAL },
					lineStyle: { color: COLOR_ACTUAL, width: 2 },
					symbolSize: 7,
					connectNulls: true,
				},
				// Solid (not dashed) to match the legacy IHBox chart — Chart.js v1
				// drew Min/Max as continuous lines.
				{
					name: "Max",
					type: "line",
					data: toSeriesData(maxes),
					itemStyle: { color: COLOR_BAND },
					lineStyle: { color: COLOR_BAND, width: 1 },
					symbolSize: 5,
					connectNulls: true,
				},
				{
					name: "Min",
					type: "line",
					data: toSeriesData(mins),
					itemStyle: { color: COLOR_BAND },
					lineStyle: { color: COLOR_BAND, width: 1 },
					symbolSize: 5,
					connectNulls: true,
				},
			],
		};
	}, [stands, visible]);

	if (stands.length === 0) return null;

	return (
		<Box>
			{/* Series toggle chips — drive the chart's series visibility via
			    legend.selected. Replaces the default ECharts legend so it's
			    obvious the items are clickable buttons. */}
			<Box
				sx={{
					display: "flex",
					gap: 0.75,
					mb: 0.75,
					justifyContent: "flex-end",
					flexWrap: "wrap",
				}}
			>
				{SERIES_NAMES.map((name) => {
					const active = visible[name];
					const color = SERIES_COLORS[name];
					return (
						<Chip
							key={name}
							size="small"
							label={name}
							onClick={() => toggle(name)}
							icon={
								<Box
									component="span"
									sx={{
										width: 9,
										height: 9,
										borderRadius: "50%",
										bgcolor: active ? color : mesfColors.grey[300],
										ml: "8px !important",
										flexShrink: 0,
										transition: "background-color 150ms ease",
									}}
								/>
							}
							sx={{
								cursor: "pointer",
								fontWeight: 600,
								fontSize: "0.72rem",
								height: 22,
								borderRadius: 1.5,
								border: `1px solid ${mesfColors.grey[200]}`,
								bgcolor: active
									? mesfColors.background.paper
									: mesfColors.grey[50],
								color: active
									? mesfColors.text.primary
									: mesfColors.text.secondary,
								transition: "all 150ms ease",
								"&:hover": {
									bgcolor: mesfColors.grey[100],
									borderColor: mesfColors.grey[300],
								},
							}}
						/>
					);
				})}
			</Box>

			<ReactECharts
				option={option}
				notMerge={false}
				lazyUpdate
				style={{ height: 280, width: "100%" }}
			/>
		</Box>
	);
};
